// Global Components
import * as React from 'react';
import Autosuggest from 'react-autosuggest';

// Helpers
import _ from 'lodash';
import {
  isDiff,
} from '@vroskus/library-helpers/dist/browser';

// Components
import InputLabel from '../InputLabel';

// Types
import type {
  $InputProps,
} from '../../../types';

type $Option = Record<string, unknown>;
type $Options = Array<$Option>;

type $OptionalProps = {
  className?: string;
  label?: string;
  labelClassName?: string;
  labelKey?: string;
  onOptionSelect?: (option: $Option) => void;
  required?: boolean;
  value?: null | string;
  valueFormatter?: (value: string) => string;
};

type $Props = $InputProps & $OptionalProps & {
  getOptions: (query: string) => Promise<$Options>;
  highlight: boolean;
  name: string;
};

type $State = {
  options: $Options;
  value?: null | string;
};

class InputTypeaheadView extends React.Component<$Props, $State> {
  static defaultProps: $OptionalProps = {
    className: undefined,
    label: undefined,
    labelClassName: undefined,
    labelKey: undefined,
    onOptionSelect: undefined,
    required: undefined,
    value: undefined,
    valueFormatter: undefined,
  };

  constructor(props: $Props) {
    super(props);

    this.state = {
      options: [],
      value: props.value,
    };
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      value,
    } = this.props;

    if (isDiff(
      value,
      prevProps.value,
    )) {
      this.setValue(value);
    }
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  setValue(value?: null | string): void {
    const {
      setValue,
    } = this.props;

    if (value) {
      setValue(value);
    }

    this.setState({
      value,
    });
  }

  renderOption(option: $Option) {
    const {
      labelKey,
    } = this.props;

    const label = _.get(
      option,
      labelKey || 'Name',
      'Option',
    ) as string;

    return (
      <div>
        {label}
      </div>
    );
  }

  render() {
    const {
      className,
      getOptions,
      highlight,
      isValid,
      label,
      labelClassName,
      labelKey,
      name,
      onOptionSelect,
      required,
      valueFormatter,
    } = this.props;
    const {
      options,
      value,
    } = this.state;

    return (
      <div className={`InputTypeahead ${className || ''}`}>
        <InputLabel
          className={labelClassName}
          label={label}
          name={name}
          required={required}
        />
        <Autosuggest
          getSuggestionValue={(option) => _.get(
            option,
            labelKey || 'Name',
            'Option',
          )}
          inputProps={{
            autoComplete: 'false',
            className: `border-${highlight && !isValid ? 'red-600' : 'gray-400'} text-sm sm:text-base placeholder-gray-600 p-2 rounded-lg border w-full focus:outline-none focus:border-blue-400`,
            id: name,
            name,
            onChange: (e, {
              newValue,
            }: {
              newValue: string;
            }) => {
              let updateValue = newValue;

              if (valueFormatter) {
                updateValue = valueFormatter(newValue);
              }

              this.setValue(updateValue);
            },
            required,
            value,
          }}
          onSuggestionsClearRequested={() => this.setState({
            options: [],
          })}
          onSuggestionSelected={(e, {
            suggestion,
          }) => {
            if (onOptionSelect && suggestion) {
              onOptionSelect(suggestion);
            }
          }}
          onSuggestionsFetchRequested={async (query) => {
            const foundOptions = await getOptions(query.value);

            await this.setStateAsync({
              options: foundOptions,
            });
          }}
          renderSuggestion={(option) => this.renderOption(option)}
          suggestions={options}
          theme={{
            container: 'relative',
            suggestion: 'btn btn_secondary btn_outlined w-full cursor-pointer cursor-pointer py-1 mb-1 whitespace-nowrap',
            suggestionsContainerOpen: 'card overflow-x-auto origin-top-left absolute left-0 z-20 mt-2 p-3',
          }}
        />
      </div>
    );
  }
}

export default InputTypeaheadView;
